/**
 * Recent interactions section on the Me tab — cross-contact feed of
 * the N most recent conversations Meridian has captured.
 *
 * Phase A grouping: one row per SESSION, not per (contact, session)
 * pair. A 3-way conversation with Henry + Bill + Bobby now shows as
 * one card listing all three participants, rather than three near-
 * duplicate rows. Taps open the unified SessionDetail view.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { ChevronRight, MapPin, Users } from "lucide-react";

import { listRecentInteractions } from "@/hooks/useSessionProcessor";
import type { Contact } from "@/lib/data";
import type { ProcessedInteraction } from "@/types/voice";
import { formatRelativeTime } from "@/utils/interactionDisplay/interactionDisplay";

import {
  formatParticipants,
  groupInteractionsBySession,
  type SessionInteractionGroup,
} from "./sessionInteractions";

const DEFAULT_LIMIT = 60;

interface RecentInteractionsSectionProps {
  contacts: Contact[];
  onSelect: (group: SessionInteractionGroup) => void;
}

export function RecentInteractionsSection({
  contacts,
  onSelect,
}: RecentInteractionsSectionProps) {
  const [rows, setRows] = useState<ProcessedInteraction[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setError(null);
    try {
      const fetched = await listRecentInteractions(DEFAULT_LIMIT);
      setRows(fetched);
    } catch (err) {
      setError(String(err));
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // Group per-contact interaction rows into per-session groups so the
  // feed reads like "one conversation = one card" regardless of how
  // many people were in it. `contacts` is a dependency so changes to
  // the contact library (names, voiceprint status) re-flow into the
  // participant labels immediately.
  const groups = useMemo<SessionInteractionGroup[] | null>(() => {
    if (!rows) return null;
    return groupInteractionsBySession(rows, contacts);
  }, [rows, contacts]);

  return (
    <section className="flex flex-col gap-3">
      <div className="flex items-baseline justify-between">
        <h2 className="text-foreground text-[17px] font-semibold">
          Recent interactions
        </h2>
        {groups && groups.length > 0 && (
          <span className="text-muted-foreground text-[12px]">
            Last {groups.length}
          </span>
        )}
      </div>

      {error && (
        <p className="text-[13px] text-destructive">Failed to load: {error}</p>
      )}

      {groups === null && !error && (
        <p className="text-muted-foreground text-[13px] italic">Loading…</p>
      )}

      {groups !== null && groups.length === 0 && (
        <div className="rounded-xl border border-[rgba(255,255,255,0.06)] bg-[rgba(255,255,255,0.02)] px-4 py-6 text-center">
          <p className="text-muted-foreground text-[14px]">
            No interactions yet.
          </p>
          <p className="text-muted-foreground mt-1 text-[12px]">
            Record a conversation and it&rsquo;ll show up here.
          </p>
        </div>
      )}

      {groups && groups.length > 0 && (
        <ul className="flex flex-col gap-1.5">
          {groups.map((group) => (
            <li key={group.sessionId}>
              <SessionInteractionCard
                group={group}
                onTap={() => onSelect(group)}
              />
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

interface SessionInteractionCardProps {
  group: SessionInteractionGroup;
  onTap: () => void;
}

function SessionInteractionCard({
  group,
  onTap,
}: SessionInteractionCardProps) {
  const title = formatParticipants(group.participantNames);
  const summary = group.summary;
  const participantCount = group.participantNames.length;
  const isGroup = participantCount > 1;
  const topics = group.topics.slice(0, 4);
  const extraTopics = Math.max(0, group.topics.length - topics.length);

  return (
    <button
      type="button"
      onClick={onTap}
      className="group relative flex w-full flex-col gap-2.5 overflow-hidden rounded-2xl border border-[rgba(255,255,255,0.06)] bg-gradient-to-br from-[rgba(255,255,255,0.025)] to-[rgba(255,255,255,0.01)] p-4 text-left transition-all active:scale-[0.995] active:bg-[rgba(255,255,255,0.04)]"
    >
      {/* Subtle accent bar on the left for visual anchor — warmer when
          the conversation is recent, cooler as it ages. */}
      <span
        className="pointer-events-none absolute inset-y-3 left-0 w-[3px] rounded-r-full bg-[rgba(201,168,76,0.4)]"
        aria-hidden
      />

      {/* Header row: title + time + chevron */}
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <p className="text-foreground truncate text-[15px] font-semibold leading-tight">
            {title}
          </p>
          <p className="text-muted-foreground mt-0.5 text-[11px]">
            {formatRelativeTime(group.createdAt)}
            {group.locationLabel && (
              <>
                {" · "}
                <span className="inline-flex items-center gap-0.5">
                  <MapPin className="h-2.5 w-2.5" />
                  {group.locationLabel}
                </span>
              </>
            )}
          </p>
        </div>
        <ChevronRight className="text-ink-4 mt-1 h-4 w-4 shrink-0" />
      </div>

      {/* Summary */}
      <p className="text-ink-2 line-clamp-2 text-[13px] leading-snug">
        {summary}
      </p>

      {/* Topic chips — the "what this was about" glance */}
      {topics.length > 0 && (
        <div className="flex flex-wrap items-center gap-1">
          {topics.map((topic, idx) => (
            <span
              key={idx}
              className="rounded-full border border-[rgba(201,168,76,0.2)] bg-[rgba(201,168,76,0.05)] px-2 py-0.5 text-[10px] text-[rgba(201,168,76,0.85)]"
            >
              {topic}
            </span>
          ))}
          {extraTopics > 0 && (
            <span className="text-[10px] text-ink-4">
              +{extraTopics} more
            </span>
          )}
        </div>
      )}

      {/* Meta row — people count. Unknown-speaker count deferred to
          SessionDetail header where we have turns loaded; showing it
          here would require a per-card backend query. */}
      {isGroup && (
        <div className="flex items-center gap-1 text-[11px] text-ink-4">
          <Users className="h-3 w-3" />
          <span>{participantCount} named</span>
          {group.peopleMentioned.length > 0 && (
            <span className="text-ink-4/70">
              {" · "}mentions {group.peopleMentioned.slice(0, 2).join(", ")}
              {group.peopleMentioned.length > 2 && ` +${group.peopleMentioned.length - 2}`}
            </span>
          )}
        </div>
      )}
    </button>
  );
}
